import {
	BreadcrumbCompat as Breadcrumb,
	IBreadcrumbItem,
} from 'components/FluentUICompat';
import {DetailsSection} from 'components';
import {LoadWrapper} from 'components/LoadWrapper';
import {ParagraphsList} from 'features/RegulatoryDocuments/components/DocumentDetails';
import {ParagraphsContextProvider} from 'features/RegulatoryDocuments/components/DocumentDetails/ParagraphsContext';
import React, {useCallback, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
	RegulatoryDocumentParagraph,
	RequirementStatus,
	UserRole,
	WorkflowStatus,
} from 'types';
import {useRequirementDetailsQuery} from '../hooks/requirementDetails.generated';
import {useRequirementDetailsPageNonEditCmds} from './useRequirementDetailsPageCmds';
import {
	RequirementDetailsResult,
	RequirementFromDetailsPage,
} from './requirementDetailsPage.types';
import {
	RequirementDetailsPageFormInfo,
	useRequirementDetailsPageEditCmdAndFormInfo as useEditCmdAndFormInfo,
} from './useRequirementDetailsPageEditCmdAndFormInfo';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {RequirementDetailsPagePanelsForEditing} from './RequirementDetailsPageFormPanels';
import {useCommand} from 'hooks/useCommand';
import {DialogInfo, useDialogState} from 'components/Dialogs';
import {useSentToApprovalCommand} from '../hooks/useSentToApprovalCommand';
import {WorkFlowDialogHandler} from './WorkFlowDialog/WorkFlowDialogHandler';
import {useUserContext} from 'authentication/UserContext';
import {useGetMyRequirementsReviewQuery} from 'features/Dashboard/hooks/myRequirementsReview.generated';
import {useRequirementsVersions} from '../hooks/useRequirementsVersions';
import {EntityListContextProvider} from '../../../components/EntityList/EntityListContext';
import {RequirementsTooltipTranslationProvider} from '../RequirementsTooltipTranslationProvider';
import {isDevEnvironment, isTestEnvironment} from 'appShell/Layout/Navigation';
import {CreateQuestionDialog} from 'components/Faq/CreateQuestionDialog';
import {useTranslation} from 'react-i18next';
import {useQuestionCountsByEntityTypeQuery} from 'components/Faq/Faq.generated';
import {RequirementsCollectionName} from 'components/Faq/EntityTypeMapper';
import {useRequirementDetailsFields} from './useRequirementDetailsFields';
import {DeleteSegmentCommand} from './DeleteSegmentCommand';
import {AssignSegmentCommand} from './AssignSegmentCommand';

export enum RequirementStatusType {
	None,
	Draft,
	SendToApproval,
	CompleteWithoutApproval,
	ClearingOK,
	ClearingNOK,
	ReadyToConfirm,
}

interface PageParams {
	requirementId: string;
}

const RequirementDetailsPage: React.FC = () => {
	const {t} = useRequirementsTranslation();
	const {t: faqT} = useTranslation('common/faq');
	const [showAudit, setShowAudit] = React.useState(false);
	const navigate = useNavigate();
	const {requirementId} = useParams() as Readonly<PageParams>;

	const [showQuestionDialog, setShowQuestionDialog] = React.useState(false);

	const {myVexClusters, isAdmin, isApprover, isVexSupport} = useUserContext();
	const {loading, data}: RequirementDetailsResult = useRequirementDetailsQuery({
		variables: {id: requirementId || ''},
	});
	const {data: questionSummaryData} = useQuestionCountsByEntityTypeQuery({
		variables: {
			entityType: RequirementsCollectionName,
			parentId: requirementId,
		},
	});
	const shouldFetchPendingApprovals = isAdmin || isApprover;
	const {data: pendingApprovals} = useGetMyRequirementsReviewQuery({
		skip: !shouldFetchPendingApprovals,
	});
	const {hasEditableChildren} = useRequirementsVersions();

	const [dialogType, setDialogType] = React.useState<RequirementStatusType>(
		RequirementStatusType.None,
	);
	const [dialogState, showDialog, hideDialog]: DialogInfo = useDialogState();

	const setAndShowDialog = useCallback(
		(statusType: RequirementStatusType) => {
			setDialogType(statusType);
			showDialog();
		},
		[showDialog],
	);

	const requirement: RequirementFromDetailsPage = React.useMemo(() => {
		const requirement = data?.requirement ?? ({} as RequirementFromDetailsPage);
		return requirement.status === RequirementStatus.Interim ||
			requirement.status === RequirementStatus.InterimCompleted
			? {
					...requirement,
					createdBy: {...requirement.createdBy, name: 'System'},
					modifiedBy: {
						...requirement.modifiedBy,
						name: 'System',
					},
			  }
			: requirement;
	}, [data]);

	const paragraphs = React.useMemo(() => {
		const assignedParagraphs = JSON.parse(
			JSON.stringify(requirement.assignedParagraphs || []),
		).filter((elem: RegulatoryDocumentParagraph) => {
			return elem?.parent?.workflow?.status !== WorkflowStatus.Modified;
		}) as RegulatoryDocumentParagraph[];

		if (assignedParagraphs.length < 2) {
			return assignedParagraphs;
		}

		return assignedParagraphs.sort((pA, pB) => {
			const regulationA = `${pA.parent.regulation?.regulationNumber} ${pA.parent.regulation?.name}`;
			const regulationB = `${pB.parent.regulation?.regulationNumber} ${pB.parent.regulation?.name}`;
			if (regulationA > regulationB) {
				return 1;
			}

			return regulationA < regulationB ? -1 : 0;
		});
	}, [requirement]);

	const auditLogs = React.useMemo(
		() => data?.requirement?.auditLog ?? [],
		[data],
	);

	const itemsWithHref: IBreadcrumbItem[] = [
		{
			text: t('RequirementsHeader'),
			key: 'requirements',
			onClick: () => navigate('/requirements'),
		},
		{
			text: `${requirement?.name}`,
			key: 'name',
		},
	];

	useRequirementDetailsPageNonEditCmds({
		requirementId,
		toggleAuditCmdFields: {setShowAudit, showAudit},
	});

	const vexClusterMatch = React.useMemo(
		() =>
			requirement?.vexClusters?.some(vc =>
				myVexClusters?.some(mvc => mvc.id === vc.id),
			),
		[requirement, myVexClusters],
	);

	const myReviews = React.useMemo(
		() =>
			pendingApprovals?.myRequirementsReview.filter(
				r => r.status === RequirementStatus.Approval,
			),
		[pendingApprovals],
	);

	const requirementsForApprovalCommand = React.useMemo(
		() => (requirement?.id ? [requirement] : []),
		[requirement],
	);

	const {workflowDialog: sentToApprovalDialog} = useSentToApprovalCommand({
		requirements: requirementsForApprovalCommand,
		vexClusterMatch: vexClusterMatch ?? false,
	});

	useCommand(
		{
			key: 'readyToConfirm',
			priority: 2,
			text: t('ReadyToConfirm'),
			iconProps: {iconName: 'WaitlistConfirm'},
			onClick: () => setAndShowDialog(RequirementStatusType.ReadyToConfirm),
			hidden: !isVexSupport,
			disabled:
				requirement.status === RequirementStatus.Approval ||
				requirement.status === RequirementStatus.ReadyForApproval ||
				requirement.status === RequirementStatus.Final ||
				requirement.status === RequirementStatus.Interim ||
				requirement.status === RequirementStatus.InterimCompleted,
			roles: [UserRole.VexSupport, UserRole.SystemAdministrator],
		},
		[requirement],
	);

	useCommand(
		{
			key: 'approveRequirement',
			priority: 2,
			text: t('AcceptApproval'),
			iconProps: {iconName: 'CheckMark'},
			onClick: () => setAndShowDialog(RequirementStatusType.ClearingOK),
			hidden: isVexSupport,
			disabled:
				requirement.status !== RequirementStatus.Approval ||
				myReviews === undefined ||
				myReviews?.length === 0,
			roles: [UserRole.ShApprover, UserRole.Vex, UserRole.SystemAdministrator],
		},
		[],
	);

	useCommand(
		{
			key: 'declineRequirement',
			priority: 2,
			text: t('DeclineApproval'),
			iconProps: {iconName: 'Cancel'},
			onClick: () => setAndShowDialog(RequirementStatusType.ClearingNOK),
			hidden: isVexSupport,
			disabled:
				requirement.status !== RequirementStatus.Approval ||
				myReviews === undefined ||
				myReviews?.length === 0,
			roles: [UserRole.ShApprover, UserRole.Vex, UserRole.SystemAdministrator],
		},
		[requirement],
	);

	useCommand(
		{
			key: 'PostQuestionButtonRequirementDetails',
			priority: 4,
			text: faqT('PostQuestionButton'),
			iconProps: {iconName: 'Chat'},
			onClick: () => setShowQuestionDialog(true),
			hidden: !(isDevEnvironment() || isTestEnvironment()) || !requirement?.id,
			disabled: !requirement?.id,
		},
		[requirement],
	);

	const initialFieldRows = useRequirementDetailsFields({
		requirement,
		showAudit,
		questionSummaryData,
	});

	const requirementHasEditableChildren = React.useMemo((): boolean => {
		return hasEditableChildren(requirement);
	}, [requirement, hasEditableChildren]);

	const pageDetails = useMemo(() => {
		return {...requirement, questionSummaryData};
	}, [requirement, questionSummaryData]);

	const {formDataLoading, ...formInfo}: RequirementDetailsPageFormInfo =
		useEditCmdAndFormInfo(data, requirementHasEditableChildren);

	if (!loading && !data?.requirement) {
		return (
			<h4 style={{marginTop: '50px', marginLeft: '20px'}}>
				{t('DocumentNotFound')}
			</h4>
		);
	}

	return (
		<RequirementsTooltipTranslationProvider>
			{showQuestionDialog && (
				<CreateQuestionDialog
					showDialog={showQuestionDialog}
					setShowDialog={setShowQuestionDialog}
				/>
			)}
			<LoadWrapper loading={loading || formDataLoading}>
				<Breadcrumb items={itemsWithHref} />
				<DetailsSection
					title={requirement.name}
					initialFieldRows={initialFieldRows}
					auditLogData={auditLogs as any}
				/>
				<RequirementDetailsPagePanelsForEditing
					requirement={requirement}
					{...formInfo}
				/>
				<h3 style={{marginLeft: 20, marginTop: 20}}>
					{t('AssignedParagraphs')}
				</h3>
				<EntityListContextProvider pageDetails={pageDetails}>
					<ParagraphsContextProvider>
						<AssignSegmentCommand requirement={requirement} />
						<DeleteSegmentCommand requirement={requirement} />
						<ParagraphsList
							paragraphs={paragraphs}
							includeRegulationAndVersionName
							fromRequirements={true}
							parentId={requirement.id}
							questionItem={requirement}
						/>
					</ParagraphsContextProvider>
				</EntityListContextProvider>
				{sentToApprovalDialog}
				<WorkFlowDialogHandler
					dialogState={dialogState}
					dialogType={dialogType}
					hideDialog={hideDialog}
					requirements={[requirement]}
				/>
			</LoadWrapper>
		</RequirementsTooltipTranslationProvider>
	);
};

export default RequirementDetailsPage;
